import type { AgencyFeeManager } from '@/state/billing/useAgencyFee';
import { Loader2 } from 'lucide-react';
import { ANNUAL_DISCOUNT_PERCENT } from 'definitions/billing-plans';
import { cn } from 'helpers/cn';
import { formatEuro } from '@/state/billing/billing-format';
import { FOCUS_RING, PillTabs } from '../../ConsolePanel';

type AgencyFeePickerProps = { fee: AgencyFeeManager };

const INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'month' },
  { label: `Annual, save ${ANNUAL_DISCOUNT_PERCENT}%`, value: 'year' },
] as const;

/**
 * Monthly or annual, the price, and the button to buy the agency fee. The fee is
 * one product and does not depend on how many clients the agency has.
 */
export const AgencyFeePicker = ({ fee }: AgencyFeePickerProps) => (
  <div className="flex flex-col gap-4">
    <PillTabs
      options={INTERVAL_OPTIONS}
      value={fee.interval}
      onChange={(value) => fee.setInterval(value === 'year' ? 'year' : 'month')}
    />

    <div className="flex flex-col gap-0.5">
      <span className="text-foreground text-[22px] font-medium leading-none tracking-[-0.02em]">
        {formatEuro(fee.monthlyEquivalent)}
        <span className="supporting text-body font-normal"> / month</span>
      </span>
      <span className="supporting">
        {fee.interval === 'year' ? `${formatEuro(fee.periodPrice)} billed yearly` : 'billed monthly'}, excl. VAT
      </span>
    </div>

    {fee.canManage && (
      <button
        type="button"
        onClick={fee.start}
        disabled={!fee.checkoutAvailable || fee.isStarting}
        className={cn(
          'bg-cta hover:bg-ctahover disabled:hover:bg-cta flex w-fit items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-body font-medium leading-none text-white transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60',
          FOCUS_RING,
        )}
      >
        {fee.isStarting && <Loader2 className="h-4 w-4 animate-spin" />}
        {fee.trialAvailable ? 'Start free trial' : 'Choose the agency fee'}
      </button>
    )}
  </div>
);
